// SHCT-582 — PRD 21 §7.4 P-4 「권한 없음 표시 규칙」, applied once for the whole app.
//
// §7.4 fixes the server-refusal wording at `접근 권한이 없습니다.` and names the variant the server
// actually sends — `권한이 없습니다.` (admin-api `CommonErrorCode.INSUFFICIENT_PERMISSION`) — as a
// 폐기된 변형. Every feature adapter builds its error from `response.data.error.message`, so the
// retired string was reaching the screen verbatim on all but one page, which is how two different
// sentences ended up appearing for the same 403.
//
// Normalising the payload here means the ~18 adapters stay untouched and cannot drift apart again:
// whatever channel a screen uses (toast, inline, modal), the words are the same.

/** The one server code §7.4 P-4 governs. */
const RBAC_DENIED_CODE = 'INSUFFICIENT_PERMISSION';

interface ServerErrorPayload {
  code?: string;
  message?: string;
}

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: { error?: ServerErrorPayload };
  };
}

/**
 * Rewrites a server RBAC refusal to the §7.4 P-4 wording, in place, and reports whether it did.
 *
 * Deliberately keyed on the error **code**, not on the 403 status. 403 is not only used for
 * permission: `ROOT_NOT_EDITABLE`, `SELF_STATUS_CHANGE_FORBIDDEN`, `SELF_WITHDRAW_FORBIDDEN`,
 * `PATIENT_CLOSE_DENIED`, `INTERVENTION_NOT_AUTHOR`, `ACCOUNT_LOCKED`, `ACCOUNT_INACTIVE`,
 * `RESERVED_ROLE`, `SEED_ROLE_NOT_DELETABLE`, `STAFF_WITHDRAWN_RESET_FORBIDDEN` and
 * `EXPORT_LINK_INVALID` all return 403 while carrying copy the operator needs, and Error Standard
 * §7.1 classes those as Business Logic Errors. Matching on the status would erase all of it.
 *
 * `EXPORT_NOT_PERMITTED` (`내보내기 권한이 없습니다.`) is a third permission sentence that §7.4 does
 * not list; it is left alone pending the customer's answer rather than folded in here.
 */
export function normalizePermissionError(error: unknown, canonicalNotice: string): boolean {
  const payload = (error as AxiosLikeError)?.response?.data?.error;
  if (!payload || payload.code !== RBAC_DENIED_CODE) return false;
  payload.message = canonicalNotice;
  return true;
}

/**
 * True when a thrown error is the RBAC refusal §7.4 P-4 governs.
 *
 * Accepts either the raw axios error or the typed feature error the adapters map it to, because the
 * two live on opposite sides of `mapError` and both reach a screen's catch block. Screens use this
 * to route the refusal to the channel §7.4 fixes — 토스트(error) — instead of whatever channel they
 * use for their own business rules.
 *
 * `EXPORT_NOT_PERMITTED` is deliberately NOT matched here, for the reason given above.
 */
export function isPermissionDenied(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const typedCode = (error as { code?: unknown }).code;
  if (typedCode === RBAC_DENIED_CODE) return true;
  return (error as AxiosLikeError).response?.data?.error?.code === RBAC_DENIED_CODE;
}
